/**
 * Composable para operações de unicidade em arrays
 * Substitui lodash uniqBy com implementação nativa mais eficiente
 */

/**
 * Remove duplicatas de um array baseado em uma propriedade específica
 * Substitui _.uniqBy do lodash
 * @param array - Array para filtrar
 * @param key - Propriedade pela qual verificar unicidade
 * @returns Array sem duplicatas
 */
export function uniqueBy<T, K extends keyof T> (array: T[], key: K): T[] {
  if (!Array.isArray(array) || array.length === 0) {
    return []
  }

  const seen = new Set<T[K]>()
  const result: T[] = []

  for (const item of array) {
    if (item != null) {
      const value = item[key]

      if (!seen.has(value)) {
        seen.add(value)
        result.push(item)
      }
    }
  }

  return result
}

/**
 * Remove duplicatas de um array simples (primitivos)
 * @param array - Array de valores primitivos
 * @returns Array sem duplicatas
 */
export function unique<T> (array: T[]): T[] {
  if (!Array.isArray(array) || array.length === 0) {
    return []
  }

  return [...new Set(array)]
}

/**
 * Remove duplicatas baseado em função de transformação
 * @param array - Array para filtrar
 * @param keyFn - Função que retorna a chave para comparação
 * @returns Array sem duplicatas
 */
export function uniqueByFn<T, K> (array: T[], keyFn: (item: T) => K): T[] {
  if (!Array.isArray(array) || array.length === 0) {
    return []
  }

  const seen = new Set<K>()
  const result: T[] = []

  for (const item of array) {
    if (item != null) {
      const key = keyFn(item)

      if (!seen.has(key)) {
        seen.add(key)
        result.push(item)
      }
    }
  }

  return result
}

/**
 * Composable que retorna as funções de unicidade
 * Útil quando você quer usar como composable reativo
 */
export function useArrayUnique (): {
  uniqueBy: typeof uniqueBy
  unique: typeof unique
  uniqueByFn: typeof uniqueByFn
  } {
  return {
    uniqueBy,
    unique,
    uniqueByFn
  }
}

/**
 * Versão reativa do uniqueBy para usar com computed
 * @param array - Ref ou computed do array
 * @param key - Propriedade pela qual verificar unicidade
 * @returns Computed com array único
 */
export function useUniqueBy<T, K extends keyof T> (
  array: () => T[],
  key: K
): () => T[] {
  return () => uniqueBy(array(), key)
}
